import {
  AppNotificationContextAction,
  AppNotificationContextActionKind,
} from "./action";
import {
  EventType,
  GetEventsResponse,
  RealtimeEventType,
  ResolveAlertEvent,
} from "../../../api/appNotifications/appNotificationInterfaces";
import {
  DownloadNotification,
  Notification,
} from "../../../components/Layout/components/Header/components/Notifications/hooks/useNotifications/useNotifications";
import {
  AlertsAndNotification,
  AlertsAndNotificationSource,
} from "../AppNotificationContext";

/**
 * Ensures ISO-8601 timestamp has milliseconds.
 * Converts "2025-11-06T01:33:44Z" to "2025-11-06T01:33:44.000Z"
 */
function normalizeTimestamp(timestamp: string): string {
  // Check if timestamp already has milliseconds (contains a dot before Z)
  if (/\.\d{3}[Z]/.test(timestamp)) {
    return timestamp;
  }

  // Add .000 before the timezone indicator (Z)
  return timestamp.replace(/([Z])/, ".000$1");
}

export type AppNotificationContextState = {
  appNotificationDomainAlerts: AlertsAndNotification[];
  appNotificationDomainNotifications: AlertsAndNotification[];
  downloadNotifications: AlertsAndNotification<
    Notification<DownloadNotification>
  >[];
  eventsLoaded: boolean;
  isRealTimeUpdatesDown: boolean;
  toggles: Record<string, boolean>;
};

export function getInitialState(
  isD2dOrderRequestNeedActionAlertEnabled: boolean = false,
  isBroadcastMessageNotificationsEnabled: boolean = false,
  isDailySurplusAlertAndNotificationEnabled: boolean = false
): AppNotificationContextState {
  return {
    appNotificationDomainAlerts: [],
    appNotificationDomainNotifications: [],
    downloadNotifications: [],
    eventsLoaded: false,
    isRealTimeUpdatesDown: false,
    toggles: {
      isD2dOrderRequestNeedActionAlertEnabled,
      isBroadcastMessageNotificationsEnabled,
      isDailySurplusAlertAndNotificationEnabled,
    },
  };
}

export function appNotificationContextReducer(
  state: AppNotificationContextState,
  action: AppNotificationContextAction
): AppNotificationContextState {
  switch (action.type) {
    case AppNotificationContextActionKind.SET_ALERTS_AND_NOTIFICATIONS_FROM_DOMAIN: {
      const events = action.payload;
      if (!events || events.length === 0) {
        return state;
      }
      const alerts = events
        .filter((event) => event.type === EventType.ALERT)
        .map(mapApiResponse);
      const notifications = events
        .filter((event) => event.type === EventType.NOTIFICATION)
        .map(mapApiResponse);

      return {
        ...state,
        appNotificationDomainAlerts: keepLocallyReadAlertsRead(
          state.appNotificationDomainAlerts,
          alerts
        ),
        appNotificationDomainNotifications: notifications,
      };
    }
    case AppNotificationContextActionKind.SET_DOWNLOAD_NOTIFICATIONS: {
      const downloadNotifications = action.payload;
      if (!downloadNotifications || downloadNotifications.length === 0) {
        return { ...state, downloadNotifications: [] };
      }

      return {
        ...state,
        downloadNotifications: downloadNotifications.map(
          mapDownloadNotification
        ),
      };
    }
    case AppNotificationContextActionKind.TOGGLE_NOTIFICATION_SUMMARY_VISIBILITY: {
      const id = action.payload;
      const toggleSummary = (item: AlertsAndNotification) =>
        item.id === id
          ? { ...item, isSummaryExpanded: !item.isSummaryExpanded }
          : item;

      const appNotificationDomainAlerts =
        state.appNotificationDomainAlerts.map(toggleSummary);
      const appNotificationDomainNotifications =
        state.appNotificationDomainNotifications.map(toggleSummary);

      return {
        ...state,
        appNotificationDomainAlerts,
        appNotificationDomainNotifications,
      };
    }
    case AppNotificationContextActionKind.SET_ALERT_AND_NOTIFICATION_FROM_APPSYNC: {
      const event = action.payload;

      if (isResolveAlertEvent(event)) {
        return handleResolveAlertEvent(state, event);
      }

      if (isGetEventsResponse(event)) {
        return handleNewEvent(state, event);
      }

      return state;
    }
    case AppNotificationContextActionKind.SET_EVENTS_LOADED: {
      return {
        ...state,
        eventsLoaded: action.payload,
      };
    }
    case AppNotificationContextActionKind.SET_IS_REAL_TIME_UPDATES_DOWN: {
      return {
        ...state,
        isRealTimeUpdatesDown: action.payload,
      };
    }
    case AppNotificationContextActionKind.MARK_EVENTS_FROM_DOMAIN_AS_READ: {
      return {
        ...state,
        appNotificationDomainAlerts: state.appNotificationDomainAlerts.map(
          (alert) =>
            action.payload.includes(alert.id)
              ? { ...alert, isRead: true }
              : alert
        ),
        appNotificationDomainNotifications:
          state.appNotificationDomainNotifications.map((notification) =>
            action.payload.includes(notification.id)
              ? { ...notification, isRead: true }
              : notification
          ),
      };
    }
    case AppNotificationContextActionKind.SET_BROADCAST_TOGGLE: {
      return {
        ...state,
        toggles: {
          ...state.toggles,
          isBroadcastMessageNotificationsEnabled: action.payload,
        },
      };
    }
    default: {
      return state;
    }
  }
}

function mapApiResponse(event: GetEventsResponse): AlertsAndNotification {
  const { id, category, payload, readStatus, timestamp, dealerId } = event;
  const normalizedTimestamp = normalizeTimestamp(timestamp);
  return {
    id: `${id}#${normalizedTimestamp}#${dealerId}`,
    type: category.toLowerCase(),
    payload,
    isRead: readStatus,
    isSummaryExpanded: false,
    createdAt: normalizedTimestamp,
    source: AlertsAndNotificationSource.APP_NOTIFICATION_SERVICE,
    dealerId,
  };
}

function mapDownloadNotification(
  downloadNotification: Notification<DownloadNotification>
): AlertsAndNotification<Notification<DownloadNotification>> {
  return {
    id: downloadNotification.payload.id,
    type: downloadNotification.payload.type ?? "",
    payload: downloadNotification,
    isRead: downloadNotification.isRead,
    createdAt: new Date(downloadNotification.createdAt).toISOString(),
    source: AlertsAndNotificationSource.DOWNLOAD_NOTIFIER,
    isSummaryExpanded: false,
    dealerId: "",
  };
}

export function isResolveAlertEvent(
  event: GetEventsResponse | ResolveAlertEvent
): event is ResolveAlertEvent {
  return (
    "realtimeEventType" in event &&
    event.type === EventType.ALERT &&
    event.realtimeEventType === RealtimeEventType.RESOLVE_ALERT
  );
}

function isGetEventsResponse(
  event: GetEventsResponse | ResolveAlertEvent
): event is GetEventsResponse {
  return !("realtimeEventType" in event);
}

export function getResolveAlertId({
  id,
  dealerId,
  timestamp,
}: ResolveAlertEvent): string {
  return `${id}#${normalizeTimestamp(timestamp)}#${dealerId}`;
}

function keepLocallyReadAlertsRead(
  currentAlerts: AlertsAndNotification[],
  fetchedAlerts: AlertsAndNotification[]
): AlertsAndNotification[] {
  const readAlertIds = new Set(
    currentAlerts.filter((alert) => alert.isRead).map((alert) => alert.id)
  );
  return fetchedAlerts.map((alert) =>
    readAlertIds.has(alert.id) ? { ...alert, isRead: true } : alert
  );
}

function handleResolveAlertEvent(
  state: AppNotificationContextState,
  event: ResolveAlertEvent
): AppNotificationContextState {
  const alertId = getResolveAlertId(event);

  return {
    ...state,
    appNotificationDomainAlerts: state.appNotificationDomainAlerts.map(
      (alert) => (alert.id === alertId ? { ...alert, isRead: true } : alert)
    ),
  };
}

function handleNewEvent(
  state: AppNotificationContextState,
  event: GetEventsResponse
): AppNotificationContextState {
  const newNotification = mapApiResponse(event);

  if (event.type === EventType.ALERT) {
    return {
      ...state,
      appNotificationDomainAlerts: [
        ...state.appNotificationDomainAlerts,
        newNotification,
      ],
    };
  }

  if (event.type === EventType.NOTIFICATION) {
    return {
      ...state,
      appNotificationDomainNotifications: [
        ...state.appNotificationDomainNotifications,
        newNotification,
      ],
    };
  }

  return state;
}
